import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import React, { FormEvent, ReactElement, useEffect, useState } from 'react'
import SvgTooltip from 'src/components/Svgs/SvgTooltip'
import { usePrevious } from 'src/hooks'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { toDollars } from 'src/utils/currency.utils'

import { settingsAnchors } from '../../settingsAnchors'
import SettingsSegmented from '../SettingsSegmented/SettingsSegmented'
import SettingsTile from '../SettingsTile/SettingsTile'
import style from './SpendLimitTile.style'

/** The subset of the limits snapshot this tile renders and fences on. */
export type SpendLimitSnapshot = {
  /** Minor units. */
  permanentLimit: number
  currency: string
  /** Optimistic-concurrency fence for the NEXT limit change. */
  version: number
}

export type SpendLimitState = 'loading' | 'ready' | 'unavailable' | 'error'

/**
 * The company line this card draws on. `availableCredit` is SIGNED — zero means
 * no headroom and negative means the line is reported overdrawn. Never clamp
 * it; the tile's copy already tells the user increases apply "within your
 * company line", so hiding a negative would contradict that.
 */
export type CompanyLineSummary = {
  name: string
  totalCredit: number
  availableCredit: number
  /**
   * The company line's OWN currency. Not the card's: the "fails closed on
   * currency disagreement" guarantee belongs to the limits contract, and
   * nothing promises this resource shares the card's currency. Formatting it
   * with the card's would silently mislabel the amount.
   */
  currency: string
}

type SpendLimitTileProps = {
  /** Grid-column class from the bento parent. */
  className?: string
  creditCard: CreditCardListModel
  /**
   * Admin/Owner.
   *
   * On the CONNECTED path this only chooses the wording ("Update limit" vs
   * "Apply") and whether the read-only company-line row and its figure appear —
   * it does NOT gate whether a change is routed for approval, because the
   * limits contract has no approval routing to route to. Only the server's
   * `APPLY_PHYSICAL_CARD_LIMIT_CHANGE` capability decides who may mutate.
   *
   * It still drives the request-vs-apply split on the unconnected preview path,
   * which mocks the eventual governed flow.
   */
  canApplyDirectly: boolean
  /**
   * EX-40631 follow-on. `true` reads and writes the Card Management limits
   * resource instead of rendering the preview form. The page owns the gate,
   * the read, and the write.
   */
  useLimitsApi?: boolean
  limits?: SpendLimitSnapshot
  limitsState?: SpendLimitState
  /**
   * Applies a PERMANENT limit change. Temporary limits have no contract — the
   * server rejects any other `kind` with CARD-MGMT.8 — so the tile never offers
   * one on the connected path.
   */
  onApplyPermanentLimit?: (permanentLimitMinorUnits: number) => void
  isApplyingLimit?: boolean
  /** Server-authored message; distinct recoveries must not collapse to one. */
  limitSaveError?: string
  limitSaveSucceeded?: boolean
  /**
   * `APPLY_PHYSICAL_CARD_LIMIT_CHANGE`. Gated independently of the read — a
   * user may legitimately see the limit and be unable to change it, so the
   * read verdict must not be treated as permission to mutate.
   */
  canApplyLimitChange?: boolean
  /** Read-only company-line summary. Composed here, not aggregated server-side. */
  companyLine?: CompanyLineSummary
}

type LimitKind = 'permanent' | 'temporary'

export default function SpendLimitTile(
  props: SpendLimitTileProps,
): ReactElement {
  const {
    className,
    creditCard,
    canApplyDirectly,
    useLimitsApi = false,
    limits,
    limitsState = 'loading',
    onApplyPermanentLimit,
    isApplyingLimit = false,
    limitSaveError,
    limitSaveSucceeded = false,
    canApplyLimitChange = false,
    companyLine,
  } = props
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  const [limitKind, setLimitKind] = useState<LimitKind>('permanent')
  const [limitAmount, setLimitAmount] = useState<number | undefined>(undefined)
  const [limitFocused, setLimitFocused] = useState(false)
  const [reason, setReason] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [amountError, setAmountError] = useState('')
  const [datesError, setDatesError] = useState('')

  // Show a formatted amount ($60,000) at rest and raw digits while editing —
  // the monetary-input pattern used across the app (see AmountInputField).
  const limitDisplayValue =
    limitAmount === undefined
      ? ''
      : limitFocused
        ? String(limitAmount)
        : formatCurrency({
            amount: limitAmount,
            currency: creditCard.currency,
            maximumFractionDigits: 0,
          })

  // `limitSaveSucceeded` is the mutation's own `isSuccess`, which stays true
  // for the life of the mutation — so without this the "Saved" note sits beside
  // the button while the user types a DIFFERENT amount, claiming a value that
  // was never sent.
  const [editedSinceSave, setEditedSinceSave] = useState(false)
  const showSavedNote = useLimitsApi && limitSaveSucceeded && !editedSinceSave

  /**
   * Clear the form once a connected save lands.
   *
   * The authoritative new figure is already rendered in the "Card limit" row
   * above, so leaving the submitted amount sitting in the input duplicates it
   * and — worse — leaves a filled, operable form whose obvious next action is
   * to re-submit the value that was just applied. Clearing leaves "Saved"
   * beside an empty form, which reads as "your change landed" rather than
   * "here is a pending change".
   *
   * Keyed on the isApplying → settled TRANSITION, not on `limitSaveSucceeded`
   * alone: that flag stays true for the life of the mutation, so depending on
   * it directly would wipe the field again on every later render, including
   * mid-typing.
   */
  const previouslyApplying = usePrevious(isApplyingLimit)
  useEffect(() => {
    if (previouslyApplying === true && !isApplyingLimit && limitSaveSucceeded) {
      setLimitAmount(undefined)
      setLimitFocused(false)
      setReason('')
      setAmountError('')
      // Not an edit — the note must survive this reset and clear on the user's
      // next keystroke instead.
      setEditedSinceSave(false)
    }
  }, [previouslyApplying, isApplyingLimit, limitSaveSucceeded])

  const handleLimitChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const digits = event.target.value.replace(/[^0-9]/g, '')
    setLimitAmount(digits === '' ? undefined : Number(digits))
    setAmountError('')
    setEditedSinceSave(true)
  }

  // On the connected path the authoritative Card Management snapshot is the
  // source; the card's own issuing limit is a different number from a different
  // system and must not be shown beside a fence taken from the snapshot.
  const limitCents = useLimitsApi
    ? limits?.permanentLimit
    : (creditCard.issuingLimitCents ?? creditCard.issuerAccountCreditLimitCents)
  // The card's currency is the display source even on the connected path. The
  // limits read fails closed when its persisted component currencies disagree
  // with the card, so the snapshot's currency can never legitimately differ —
  // and it arrives as a bare wire string rather than the typed ExtendCurrency
  // the formatter takes.
  const limitCurrency = creditCard.currency
  // The company line carries its own currency and is a separate resource with
  // its own failure mode. `currencyFormatter` validates the code at runtime and
  // falls back, so the wire string is safe to hand it directly.
  const companyLineCurrency = (companyLine?.currency ??
    creditCard.currency) as typeof creditCard.currency
  // No invented fallback: when the card carries no limit we say so rather than
  // rendering a plausible-looking number the user could act on.
  const formattedLimit =
    limitCents !== undefined
      ? formatCurrency({
          amount: toDollars(limitCents),
          currency: limitCurrency,
          maximumFractionDigits: 0,
        })
      : undefined

  /**
   * "Not available" is a CLAIM about this card — the server said the limit is
   * denied or the card is not enrolled. It is not what an unresolved read looks
   * like, and saying it while capabilities and `/limits` are still in flight
   * made the row flash "Not available" and then swap to the real figure.
   *
   * The unconnected path has no round trip: `limitCents` comes off the credit
   * card synchronously, so an absent value there really is an absent limit.
   */
  const limitValueText = ((): string => {
    if (!useLimitsApi) return formattedLimit ?? translate('Not available')
    if (limitsState === 'loading') return translate('Loading')
    // A failed read is not an absent limit either — the body carries the
    // retry copy, so this must not assert a value we never received.
    if (limitsState === 'error') return translate('Unavailable')
    return formattedLimit ?? translate('Not available')
  })()

  /**
   * What the submit button honestly promises.
   *
   * A DECREASE applies instantly for every role — there is nothing to approve
   * when a card asks for less headroom. The flat
   * `canApplyDirectly ? 'Update limit' : 'Submit request'` therefore told a Card
   * Manager lowering their own limit that they were filing a request, and then
   * applied it immediately. The design guards this explicitly: "the button never
   * promises 'Submit request' for a decrease, which now applies instantly."
   *
   * Compared in dollars on both sides — `limitAmount` is what the user typed,
   * `limitCents` is the snapshot in minor units.
   */
  const currentLimitDollars =
    limitCents !== undefined ? toDollars(limitCents) : undefined
  const isDecrease =
    limitAmount !== undefined &&
    currentLimitDollars !== undefined &&
    limitAmount < currentLimitDollars
  // On the CONNECTED path every permitted change is instant: the limits
  // contract has no approval routing at all — `LimitChangeResponse.status` is
  // `SUCCEEDED` only — so holding APPLY_PHYSICAL_CARD_LIMIT_CHANGE means the
  // POST applies immediately regardless of role. EX-40509 makes this explicit:
  // "Do not present this permanent direct-change flow as 'Submit request', and
  // do not expose the deferred temporary-limit or approval-request path."
  const appliesInstantly = useLimitsApi || canApplyDirectly || isDecrease
  const submitLabel = appliesInstantly
    ? canApplyDirectly
      ? translate('Update limit')
      : translate('Apply')
    : translate('Submit request')

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()

    const amountIsValid = limitAmount !== undefined && limitAmount > 0
    setAmountError(
      amountIsValid ? '' : translate('Enter a limit greater than zero.'),
    )

    const datesAreValid =
      limitKind === 'permanent' ||
      (startDate !== '' && endDate !== '' && startDate <= endDate)
    setDatesError(
      datesAreValid ? '' : translate('Enter a start and end date, in order.'),
    )

    if (!amountIsValid || !datesAreValid) {
      return
    }

    if (useLimitsApi) {
      // NOT cleared here: the isApplying -> settled effect owns that. Clearing
      // before the call meant a submit the page dropped (no snapshot, so no
      // mutation) re-showed "Saved" beside an amount that was never sent.
      // Minor units at the transport boundary — the contract's money is always
      // an integer in minor units, never a float.
      onApplyPermanentLimit?.(Math.round((limitAmount ?? 0) * 100))
      return
    }
    setSubmitted(true)
  }

  const resetForm = (): void => {
    setSubmitted(false)
    setLimitAmount(undefined)
    setLimitFocused(false)
    setReason('')
    setStartDate('')
    setEndDate('')
    setAmountError('')
    setDatesError('')
  }

  // Temporary limits are deliberately absent on the connected path: the
  // contract supports `kind: "PERMANENT"` only and rejects anything else with
  // CARD-MGMT.8, so offering the control would promise a change the server
  // cannot make.
  const limitKindOptions = [
    { label: translate('Permanent'), value: 'permanent' },
    ...(useLimitsApi
      ? []
      : [{ label: translate('Temporary'), value: 'temporary' }]),
  ]

  return (
    <SettingsTile
      className={className}
      id={settingsAnchors.spendLimit}
      title={translate('Spend limit')}
    >
      <div className="SpendLimitTile" css={style}>
        <div className="SpendLimitTile-kv">
          <span className="SpendLimitTile-kvLabel">
            {translate('Card limit')}
            {/*
              The design shows this pill unconditionally, but its prototype has
              no read-only state. Claiming "Adjustable" two lines above "You do
              not have permission to change this card's spend limit" is a
              contradiction, so on the connected path it follows the real
              mutation capability.
            */}
            {formattedLimit !== undefined &&
              (!useLimitsApi || canApplyLimitChange) && (
                <Pill size="small" color="highlight">
                  {translate('Adjustable')}
                </Pill>
              )}
          </span>
          <span
            className="SpendLimitTile-kvValue strong"
            data-testid="spend-limit-current"
          >
            {limitValueText}
          </span>
        </div>

        {/*
          Design shows this row to Admin/Owner only, labelled "Company line"
          (not the line's own name) with the value read "X available of Y".

          DELTA raised rather than invented: the contract returns a display
          `name` for the line ("Acme Corporate Line") that the design has no
          slot for, so it is carried on the model but not rendered. If product
          wants it shown, it needs a design answer for where.
        */}
        {companyLine !== undefined && canApplyDirectly && (
          <div className="SpendLimitTile-kv" data-testid="company-line">
            <span className="SpendLimitTile-kvLabel">
              {translate('Company line')}
              {/*
                A real <button>, not ext-ui's TooltipIcon: that primitive
                renders its trigger as a plain <div>, so MUI never receives
                focus and the content is hover-only (WCAG 2.1.1 / 1.4.13).
                Fixing the primitive would add a tab stop to all 36 of its
                consumers, so this surface uses a focusable trigger directly.
                The aria-label carries the same sentence, so assistive tech
                gets it without opening the tooltip at all.
              */}
              <Tooltip
                tooltipContent={translate(
                  'The commercial line this card draws from. Read-only.',
                )}
              >
                <button
                  type="button"
                  className="SpendLimitTile-kvInfo"
                  aria-label={translate(
                    'The commercial line this card draws from. Read-only.',
                  )}
                  data-testid="company-line-info"
                >
                  <SvgTooltip width={14} height={14} viewBox="0 0 24 24" />
                </button>
              </Tooltip>
            </span>
            <span className="SpendLimitTile-kvValue">
              {translate('${available} available of ${total}', {
                available: formatCurrency({
                  amount: toDollars(companyLine.availableCredit),
                  currency: companyLineCurrency,
                  maximumFractionDigits: 0,
                }),
                total: formatCurrency({
                  amount: toDollars(companyLine.totalCredit),
                  currency: companyLineCurrency,
                  maximumFractionDigits: 0,
                }),
              })}
            </span>
          </div>
        )}

        <h4 className="SpendLimitTile-heading">
          {translate('Adjust the spend limit')}
        </h4>
        <p className="SpendLimitTile-body">
          {useLimitsApi
            ? // No governed/approval branch on the connected path: nothing is
              // routed for approval, so describing it that way would promise a
              // step the server never performs. The company-line figure is
              // interpolated only where that read resolved AND the row itself is
              // shown, rather than printing a placeholder the user could act on.
              companyLine !== undefined && canApplyDirectly
              ? translate(
                  'This card limit is a spend control within your company line. Decreases apply instantly; raising it within your available line (${available}) also applies instantly and is recorded in Activity.',
                  {
                    available: formatCurrency({
                      // The LINE's own currency, not the card's — nothing
                      // promises they agree (see companyLineCurrency).
                      amount: toDollars(companyLine.availableCredit),
                      currency: companyLineCurrency,
                      maximumFractionDigits: 0,
                    }),
                  },
                )
              : translate(
                  'This card limit is a spend control within your company line. Decreases apply instantly; raising it within your available line also applies instantly and is recorded in Activity.',
                )
            : canApplyDirectly
              ? translate(
                  'Once connected: decreases apply instantly, and increases are applied within your company line and recorded in Activity.',
                )
              : translate(
                  'Once connected: decreases apply instantly, and increases are governed — routed to an Admin or Owner to approve, then applied within your company line, and recorded in Activity.',
                )}
        </p>

        {!useLimitsApi && (
          <p className="SpendLimitTile-body" data-testid="spend-limit-preview">
            {translate(
              'Limit changes are not connected yet. This form is a preview — nothing is sent and no limit changes.',
            )}
          </p>
        )}

        {useLimitsApi && limitsState === 'ready' && !canApplyLimitChange ? (
          <p
            className="SpendLimitTile-body"
            data-testid="spend-limit-read-only"
          >
            {translate(
              'You do not have permission to change this card\u2019s spend limit.',
            )}
          </p>
        ) : useLimitsApi && limitsState === 'unavailable' ? (
          <p
            className="SpendLimitTile-body"
            data-testid="spend-limit-unavailable"
          >
            {translate(
              'Spend limit is not available for this card. It is not enrolled in card management, or your role cannot view it.',
            )}
          </p>
        ) : useLimitsApi && limitsState === 'error' ? (
          <p
            className="SpendLimitTile-body"
            role="alert"
            data-testid="spend-limit-load-error"
          >
            {translate(
              "We couldn't load this card's spend limit. Refresh to try again.",
            )}
          </p>
        ) : useLimitsApi && limitsState === 'loading' ? (
          <p className="SpendLimitTile-body" data-testid="spend-limit-loading">
            {translate('Loading')}
          </p>
        ) : submitted ? (
          <div className="SpendLimitTile-confirmation">
            <span className="SpendLimitTile-confirmationHeading">
              {appliesInstantly
                ? translate('Nothing was applied')
                : translate('Nothing was submitted')}
            </span>
            <p className="SpendLimitTile-confirmationBody">
              {appliesInstantly
                ? translate(
                    'This is a preview of the update flow. No change was made and the card limit is unchanged.',
                  )
                : translate(
                    'This is a preview of the request flow. No request was sent and the card limit is unchanged.',
                  )}
            </p>
            <Button
              variant="no-style"
              onClick={resetForm}
              data-testid="spend-limit-reset"
            >
              {translate('Start over')}
            </Button>
          </div>
        ) : (
          <form className="SpendLimitTile-form" onSubmit={handleSubmit}>
            <SettingsSegmented
              ariaLabel={translate('Limit duration')}
              options={limitKindOptions}
              value={limitKind}
              onChange={(next) => setLimitKind(next as LimitKind)}
            />
            <ExtUITextInput
              variant="small"
              label={
                limitKind === 'temporary'
                  ? translate('Temporary limit')
                  : translate('New limit')
              }
              placeholder={limitKind === 'temporary' ? '$75,000' : '$60,000'}
              value={limitDisplayValue}
              inputMode="numeric"
              requirement="required"
              errorText={amountError}
              onFocus={() => setLimitFocused(true)}
              onBlur={() => setLimitFocused(false)}
              onChange={handleLimitChange}
              data-testid="spend-limit-amount"
            />
            {limitKind === 'temporary' && (
              <div className="SpendLimitTile-dates">
                <ExtUITextInput
                  variant="small"
                  label={translate('Start')}
                  type="date"
                  requirement="required"
                  errorText={datesError}
                  value={startDate}
                  onChange={(event) => {
                    setStartDate(event.target.value)
                    setDatesError('')
                  }}
                />
                <ExtUITextInput
                  variant="small"
                  label={translate('End')}
                  type="date"
                  requirement="required"
                  value={endDate}
                  onChange={(event) => {
                    setEndDate(event.target.value)
                    setDatesError('')
                  }}
                />
              </div>
            )}
            <ExtUITextInput
              variant="small"
              label={translate('Reason')}
              placeholder={translate('Q3 travel & events budget')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <div className="SpendLimitTile-formActions">
              <Button
                type="submit"
                dropShadow={false}
                loading={useLimitsApi ? isApplyingLimit : undefined}
                data-testid="spend-limit-submit"
              >
                {submitLabel}
              </Button>
              {showSavedNote && (
                <span
                  className="SpendLimitTile-savedNote"
                  data-testid="spend-limit-saved"
                >
                  {translate('Saved')}
                </span>
              )}
              {useLimitsApi && limitSaveError !== undefined && (
                <span
                  className="SpendLimitTile-errorNote"
                  role="alert"
                  data-testid="spend-limit-save-error"
                >
                  {limitSaveError}
                </span>
              )}
            </div>
          </form>
        )}
      </div>
    </SettingsTile>
  )
}
